<template>
  <div class="migration-wrap">
    <div class="step-bar">
      <template v-for="(s, i) in steps">
        <div :key="'step-' + i" class="step-item">
          <div :class="['step-dot', stepClass(i)]">
            <i v-if="i < currentStep" class="el-icon-check"></i>
            <span v-else>{{ i + 1 }}</span>
          </div>
          <span :class="['step-label', stepClass(i)]">{{ s }}</span>
        </div>
        <div
          v-if="i < steps.length - 1"
          :key="'line-' + i"
          class="step-line"
        ></div>
      </template>
    </div>

    <StepType
      v-if="currentStep === 0"
      :selected="selectedModule"
      @select="onModuleSelected"
      @next="goNext"
    />

    <StepUpload
      v-if="currentStep === 1"
      :module="selectedModule"
      @uploaded="onUploaded"
      @back="goBack"
    />

    <StepMapping
      v-if="currentStep === 2"
      :module="selectedModule"
      :session-id="sessionId"
      :file-columns="fileColumns"
      :preview-rows="previewRows"
      :auto-map="autoMap"
      :target-fields="targetFields"
      :required-fields="requiredFields"
      :field-labels="fieldLabels"
      @ready="onMappingReady"
      @back="goBack"
    />

    <StepPreview
      v-if="currentStep === 3"
      :module="selectedModule"
      :session-id="sessionId"
      :preview-results="previewResults"
      :total-rows="totalRows"
      :error-count="errorCount"
      :ok-count="okCount"
      :options.sync="options"
      @start="onStartImport"
      @back="goBack"
    />

    <StepImporting
      v-if="currentStep === 4"
      :session-id="sessionId"
      :module="selectedModule"
      @restart="resetWizard"
      @goto-module="onGotoModule"
    />
  </div>
</template>

<script>
import api from "@/config/Api";
import StepType from "./StepType.vue";
import StepUpload from "./StepUpload.vue";
import StepMapping from "./StepMapping.vue";
import StepPreview from "./StepPreview.vue";
import StepImporting from "./StepImporting.vue";

export default {
  name: "MigrationModule",
  components: { StepType, StepUpload, StepMapping, StepPreview, StepImporting },
  data() {
    return {
      steps: ["Tipo", "Archivo", "Mapeo", "Vista previa", "Importar"],
      currentStep: 0,
      selectedModule: null,
      sessionId: null,
      fileColumns: [],
      previewRows: [],
      totalRows: 0,
      autoMap: {},
      targetFields: [],
      requiredFields: [],
      fieldLabels: {},
      previewResults: [],
      errorCount: 0,
      okCount: 0,
      options: {
        skip_errors: true,
        update_existing: false,
        create_missing_clients: true
      }
    };
  },
  methods: {
    stepClass(i) {
      if (i < this.currentStep) return "done";
      if (i === this.currentStep) return "active";
      return "pending";
    },
    onModuleSelected(mod) {
      this.selectedModule = mod;
    },
    goNext() {
      if (this.currentStep < 4) this.currentStep++;
    },
    goBack() {
      if (this.currentStep > 0) this.currentStep--;
    },
    onUploaded(payload) {
      this.sessionId = payload.session_id;
      this.fileColumns = payload.columns || [];
      this.previewRows = payload.preview || [];
      this.totalRows = payload.total_rows || 0;
      this.autoMap = payload.auto_map || {};
      this.targetFields = payload.target_fields || [];
      this.requiredFields = payload.required_fields || [];
      this.fieldLabels = payload.field_labels || {};

      if (payload.auto_mapped) {
        this.runPreviewAndAdvanceTwo();
      } else {
        this.currentStep = 2;
      }
    },
    async runPreviewAndAdvanceTwo() {
      try {
        const mapping = { ...this.autoMap };
        const {
          data
        } = await api().post(
          `/integrations/imports/${this.sessionId}/preview`,
          { mapping, options: this.options }
        );
        this.previewResults = data.preview || [];
        this.errorCount = data.error_count || 0;
        this.okCount = data.ok_count || 0;
        this.totalRows = data.total_rows || this.totalRows;
        this.currentStep = 3;
      } catch (e) {
        this.$notifications.error({
          message: "No se pudo generar la vista previa."
        });
      }
    },
    async onMappingReady(mapping) {
      try {
        const {
          data
        } = await api().post(
          `/integrations/imports/${this.sessionId}/preview`,
          { mapping, options: this.options }
        );
        this.previewResults = data.preview || [];
        this.errorCount = data.error_count || 0;
        this.okCount = data.ok_count || 0;
        this.totalRows = data.total_rows || this.totalRows;
        this.currentStep = 3;
      } catch (e) {
        this.$notifications.error({
          message: "No se pudo generar la vista previa."
        });
      }
    },
    async onStartImport() {
      try {
        await api().post(`/integrations/imports/${this.sessionId}/run`, {});
        this.currentStep = 4;
      } catch (e) {
        this.$notifications.error({
          message: "No se pudo iniciar la importación."
        });
      }
    },
    onGotoModule() {
      const routes = {
        catalog: "/dashboard/catalogue/products",
        directory: "/dashboard/clients",
        invoices: "/dashboard/sales/invoices"
      };
      const path = routes[this.selectedModule];
      if (path) this.$router.push(path).catch(() => {});
      this.$emit("close");
    },
    resetWizard() {
      this.currentStep = 0;
      this.selectedModule = null;
      this.sessionId = null;
      this.fileColumns = [];
      this.previewRows = [];
      this.totalRows = 0;
      this.autoMap = {};
      this.targetFields = [];
      this.requiredFields = [];
      this.fieldLabels = {};
      this.previewResults = [];
      this.errorCount = 0;
      this.okCount = 0;
    }
  }
};
</script>

<style lang="scss" scoped>
.migration-wrap {
  max-width: 980px;
  margin: 0 auto;
  padding: 1.5rem 0 2rem;
}

.step-bar {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 2rem;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;

  &.done {
    background: #3ecfb4;
    color: #fff;
  }
  &.active {
    background: #2d2b55;
    color: #fff;
  }
  &.pending {
    background: #f1efe8;
    color: #888;
    border: 1px solid #d3d1c7;
  }
}

.step-line {
  flex: 1;
  height: 1px;
  background: #e4eaf6;
  margin: 0 8px;
  min-width: 12px;
}

.step-label {
  font-size: 12px;
  color: #888;

  &.active {
    color: #2d2b55;
    font-weight: 600;
  }
  &.done {
    color: #3ecfb4;
  }
}
</style>
